import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { DefaultTable } from 'Components/common/DefaultTable';
import { Main as Container } from 'Components/Main';
import { getTableHeader } from './table-header.js';
import { resolveActionKeysToRegex } from './action-key-resolver.js';
import { PageLoaderList } from 'Components/PageLoader/List';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { NoData } from 'Components/NoData';
import { getAuditLogPaged } from 'services/audit-logs-service';
import { Controller, useForm } from 'react-hook-form';
import { SearchIcon } from 'Components/common/Icons';
import { DefaultPagination } from 'Components/common/DefaultPagination';
import { FilterPanel } from 'Components/common';
import { PageHeader, Select, TextField } from 'Components/ui';
import * as S from './styles';

export const AuditLogs = () => {
  const selectedTenant = useSelector(useAuthTenant);
  const { t, i18n } = useTranslation('audit');
  const tableHeader = getTableHeader(t, i18n.resolvedLanguage ?? i18n.language);
  const [refetch, setRefetch] = useState(false);
  const [page, setPage] = useState(1);

  const { register, control, reset, watch, getValues } = useForm({
    defaultValues: {
      typeFilter: '',
      searchContent: '',
      startDate: '',
      endDate: '',
    },
  });

  const getContentSearch = () => {
    if (watch('typeFilter') === '') return '';
    const raw = watch('searchContent');
    if (!raw) return '';

    if (watch('typeFilter') === 'Ação') {
      const regex = resolveActionKeysToRegex(i18n, raw);
      if (regex) return regex;
    }

    return raw;
  };

  const getValueTypeFilter = () => {
    switch (watch('typeFilter')) {
      case 'Usuário':
        return 'user';
      case 'Ação':
        return 'action';
      case 'Resposta':
        return 'return';
      default:
        return '';
    }
  };

  const getValueStartDateFilter = () => {
    const v = getValues('startDate');
    return (v == null || v === '') ? null : v;
  };

  const getValueEndDateFilter = () => {
    const v = getValues('endDate');
    return (v == null || v === '') ? null : v;
  };

  const {
    isLoading,
    error,
    data: pagedLogs,
  } = useQuery(['getLogs', refetch, page], async () => {
    if (selectedTenant.slug === undefined) return [];
    const search = getContentSearch();
    const filterValue = getValueTypeFilter();
    const filterStartDate = getValueStartDateFilter();
    const filterEndDate = getValueEndDateFilter();
    const result = await getAuditLogPaged(
      selectedTenant.slug,
      page,
      15,
      filterValue,
      search,
      filterStartDate,
      filterEndDate,
    );
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  return (
    <Container>
      <PageHeader
        title={t('audit:page.title')}
        segments={[
          { label: t('common:breadcrumbs.generalSettings') },
          { label: t('audit:page.title') },
        ]}
      />
      {error && <div>Ocorreu um erro ...</div>}
      {!error && (
        <>
          <FilterPanel onClear={() => { reset(); setRefetch(!refetch); }}>
            <FilterPanel.Item>
              <Controller
                control={control}
                name="startDate"
                render={({ field }) => (
                  <TextField
                    type="date"
                    label="Data inicial"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    {...field}
                    value={field.value || ''}
                  />
                )}
              />
            </FilterPanel.Item>
            <FilterPanel.Item>
              <Controller
                control={control}
                name="endDate"
                render={({ field }) => (
                  <TextField
                    type="date"
                    label="Data final"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    {...field}
                    value={field.value || ''}
                  />
                )}
              />
            </FilterPanel.Item>
            <FilterPanel.Item>
              <Controller
                control={control}
                name="typeFilter"
                render={({ field }) => (
                  <Select
                    name="typeFilter"
                    label="Filtrar por"
                    value={field.value}
                    onChange={field.onChange}
                    options={[
                      { value: '', label: 'Selecione o campo' },
                      ...['Usuário', 'Ação', 'Resposta'].map((item) => ({ value: item, label: item })),
                    ]}
                  />
                )}
              />
            </FilterPanel.Item>
            <FilterPanel.Item>
              <TextField
                {...register('searchContent')}
                label="Buscar"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </FilterPanel.Item>
            <FilterPanel.Item>
              <S.FilterButton variant="primary" endIcon={<SearchIcon size={16} fill="white" />}
                disabled={watch('typeFilter') === ''}
                onClick={() => { setRefetch(!refetch); }}>
                Filtrar
              </S.FilterButton>
            </FilterPanel.Item>
          </FilterPanel>
          {!isLoading && pagedLogs.data.length === 0 && <NoData description={'Não há logs para exibir'} />}
          {!isLoading && pagedLogs.data?.length > 0 && (
            <div className="container mt-3">
              <DefaultTable
                rows={tableHeader}
                cells={pagedLogs.data}
                onClickDelete={() => {}}
                hideDelete
                hideEdit
                hideShow
                link="logs"
              />
              <S.PaginationWrapper>
                <DefaultPagination count={pagedLogs.totalPages} page={page} color="primary"
                  showFirstButton showLastButton
                  onChange={(e, selectedPage) => { setPage(selectedPage); }} />
              </S.PaginationWrapper>
            </div>
          )}
        </>
      )}
      {isLoading && <PageLoaderList></PageLoaderList>}
    </Container>
  );
};
